/**
 * The message text rule, the server's (agent-1 C70, `contracts/chat.py`), so the
 * composer's counter and its "send" button agree with what the server will
 * accept rather than with `string.length`.
 *
 * 1. CRLF and a lone CR become LF.
 * 2. Control (Cc), format (Cf) and surrogate (Cs) characters are removed, and so
 *    is the whole tag block U+E0000–E007F, KEEPING `\n`, `\t`, ZWNJ (U+200C) and
 *    ZWJ (U+200D) -- the joiners carry meaning in several scripts and emoji.
 * 3. What is left must not be blank (joiners alone do not count) and must be at
 *    most 1,000 code points in its NFC form.
 *
 * The server stores the stripped text, not the NFC form; NFC is only how it
 * counts. A lone surrogate cannot survive a JS string's trip through
 * `JSON.stringify` and UTF-8 anyway, but stripping it here keeps the count honest.
 */
export const MAX_MESSAGE_CODE_POINTS = 1_000

/** The counter appears from here (spec W2). */
export const COUNTER_FROM = 900

const KEEP = new Set(['\n', '\t', '\u200c', '\u200d'])
const STRIPPED = /[\p{Cc}\p{Cf}\p{Cs}\u{E0000}-\u{E007F}]/u

export function stripMessage(text: string): string {
  return [...text.replace(/\r\n?/g, '\n')].filter((ch) => KEEP.has(ch) || !STRIPPED.test(ch)).join('')
}

/** Code points of the NFC form of the stripped text: the server's count. */
export function messageLength(text: string): number {
  return [...stripMessage(text).normalize('NFC')].length
}

/** Whether the server would accept this text (not blank, not too long). */
export function isSendable(text: string): boolean {
  const stripped = stripMessage(text)
  const visible = stripped.replace(/[\s\u200c\u200d]/gu, '')
  return visible !== '' && [...stripped.normalize('NFC')].length <= MAX_MESSAGE_CODE_POINTS
}
